/* SPR v2 — shared constants. Route params are placeholders until BE confirms them. */

export const SPR_VERSION = {
  AUTO: "auto",
  DRAFT: "draft",
};

/** How each version is named in the footer dropdown and confirmations. */
export const VERSION_LABEL = {
  draft: "Last saved version",
  auto: "Automated version",
};

/** Phones (below tablet). The review page is read-only here, like Preview (user, 2026-10-09);
 * keep in step with the 768px tablet breakpoint in SprHeader's banner rule. */
export const PHONE_MEDIA_QUERY = "(max-width: 767px)";

/** How a page consumes the report: `edit` keeps hidden items, `view` strips them. */
/** `type` of a POST /spr/draft/:reportId call: `initial` on the system (automated) report —
 * BE copies it into a new draft — and `draft` on that draft from then on. */
export const SPR_SAVE_TYPE = { INITIAL: "initial", DRAFT: "draft" };

/* Save errors that mean the report on screen is stale (someone replaced or removed the draft):
 * tell the user and reload instead of a plain retry. INVALID_REPORT_FOR_TYPE is deliberately
 * not here: it means this page sent the wrong save type for the report, so it is shown as a
 * normal save error with BE's message rather than "changed by someone else". */
export const SPR_STALE_SAVE_CODES = ["REPORT_SUPERSEDED", "REPORT_NOT_FOUND"];

/** Which section a new topic is added to: an ongoing topic or a completed one. */
export const NEW_TOPIC_SECTION = { CURRENT: "current", COMPLETED: "completed" };

/** SPR History popup: `sentAt` is shown in IST, split into these Date and Time columns. */
export const SPR_HISTORY_TIMEZONE = "Asia/Kolkata";
export const SPR_HISTORY_DATE_FORMAT = "DD-MM-YYYY";
export const SPR_HISTORY_TIME_FORMAT = "hh:mm A";

/** The sender's raw admin_login role → the Role column label (BE sends no label). An unknown
 * role shows as sent. */
export const SPR_SENDER_ROLE_LABEL = {
  cs_person: "Learning manager",
  mentor: "Mentor",
  admin: "Admin",
  Moderator: "Moderator",
};

/** How each `delivery` channel of a send is named in the partial-delivery warning. */
export const SPR_DELIVERY_CHANNEL_LABEL = {
  email: "email",
  whatsapp: "WhatsApp",
};

/** Error statuses that mean "you may not see this report": the page shows NoPermission. */
export const FORBIDDEN_STATUSES = [401, 403];

export const SPR_MODE = {
  EDIT: "edit",
  VIEW: "view",
};

export const SPR_SUBJECT_TYPE = {
  MATH: "math",
  CODING: "coding",
};

export const SPR_REVIEW_ROUTE = "/spr-review/:studentId/:courseId";
export const SPR_PREVIEW_ROUTE = "/spr-review/:studentId/:courseId/preview";
/** Public, no login: the parent's link from email / WhatsApp, `${REACT_APP_SPR}/sent/<urlHash>`
 * (REACT_APP_SPR = the SPR host + `/#/spr`, e.g. https://spr.dev.codeyoung.com/#/spr). Lives in
 * App.jsx's public <Switch>; the page itself checks epicenter_spr_v2_send (no login, so no route
 * `flag:`). */
export const SPR_PARENT_ROUTE = "/spr/sent/:urlHash";
/** The older (v1) SPR report link, also public. */
export const SPR_V1_REPORT_ROUTE = "/spr/:studentId/:batchId/:courseId";
/** BE's token format; anything else is "not found" without a request. */
export const SPR_URL_HASH_PATTERN = /^[A-Za-z0-9_-]{21,64}$/;

/** A report exactly as it was sent to the parent, opened from the SPR History popup. */
export const SPR_SENT_PREVIEW_ROUTE = "/spr-sent/:sentId";

/** Paths rendered without the Epicenter sidebar/header (App.jsx early exit). */
export const SPR_NO_LAYOUT_ROUTES = [SPR_REVIEW_ROUTE, SPR_PREVIEW_ROUTE, SPR_SENT_PREVIEW_ROUTE];

/** Display formats for BE ISO timestamps. */
export const SPR_DATE_TIME_FORMAT = "DD MMM YYYY, hh:mm A";
export const SPR_DATE_FORMAT = "DD MMM YYYY";

/** Topic-level tag keys → label + Tag variant. Labels fall back to these if BE sends none. */
export const TOPIC_TAG = {
  going_well: { label: "Going well", variant: "success" },
  behind_schedule: { label: "Behind schedule", variant: "warning" },
  needs_focus: { label: "Needs focus", variant: "alert" },
  needs_practice: { label: "Needs practice", variant: "danger" },
};

/** Learning-objective level keys → label + Tag variant. */
export const LO_LEVEL = {
  needs_support: { label: "Needs support", variant: "warningSoft" },
  on_track: { label: "On track", variant: "successSoft" },
  mastering: { label: "Mastering", variant: "infoSoft" },
  yet_to_start: { label: "Yet to start", variant: "neutralSoft" },
};

export const DEFAULT_TOPIC_TAG = "going_well";
export const DEFAULT_LO_LEVEL = "on_track";

/** Longest summary BE accepts (`aiSummary.desc`, 1–2000 characters after trimming). */
export const SPR_SUMMARY_MAX_LENGTH = 2000;

/** View mode shows this many completed topics as cards; the rest become a name list. */
export const COMPLETED_TOPICS_CARD_LIMIT = 4;

export const PROJECT_GROUPS = [
  { key: "mini", title: "Mini projects", statLabel: "Mini project" },
  { key: "major", title: "Major projects", statLabel: "Major project" },
  { key: "final", title: "Final projects", statLabel: "Final project" },
];

/** Hash-router link opened in a new tab, e.g. `/#/spr-review/1/maths?version=auto`. */
export const buildSprReviewUrl = ({ studentId, courseId, version }) =>
  `/#/spr-review/${encodeURIComponent(studentId ?? "")}/${encodeURIComponent(
    courseId ?? "",
  )}?version=${version}`;

export const buildSprSentPreviewUrl = (sentId) => `/#/spr-sent/${encodeURIComponent(sentId ?? "")}`;

export const buildSprPreviewUrl = ({ studentId, courseId, version }) =>
  `/#/spr-review/${encodeURIComponent(studentId ?? "")}/${encodeURIComponent(
    courseId ?? "",
  )}/preview?version=${version}`;
